import { MessageSquare } from "lucide-react";
import type { ReactNode } from "react";

import { FEEDBACK_OVERLAY_ATTR } from "../../lib/screenshot";
import { FeedbackDialog } from "../feedback/FeedbackDialog";
import { GlobalLoadingIndicator } from "../ui/LoadingIndicator";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { AppFooter } from "./AppFooter";
import { ImportProgressBanner } from "./ImportProgressBanner";
import { ImportSuccessBanner } from "./ImportSuccessBanner";
import { Sidebar } from "./Sidebar";
import { Toolbar } from "./Toolbar";

export interface AppShellProps {
  children: ReactNode;
}

// Global feedback affordance reachable on every page (Unit 4). The
// data-feedback-overlay attr keeps the button itself out of any screenshot the
// dialog captures. The sidebar's "Help us Improve" entry is kept too (operator
// decision) -- both open the same dialog.
function FloatingFeedbackButton() {
  return (
    <div {...{ [FEEDBACK_OVERLAY_ATTR]: "" }} className="fixed bottom-5 right-5 z-40">
      <FeedbackDialog
        trigger={
          <button
            type="button"
            aria-label="Send feedback"
            title="Send feedback"
            className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-on-primary shadow-lg transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2"
          >
            <MessageSquare aria-hidden="true" className="h-5 w-5" />
          </button>
        }
      />
    </div>
  );
}

export function AppShell({ children }: AppShellProps) {
  // data-scroll-restoration-id opts the app's real scroll container into
  // TanStack Router scroll restoration. The shell is pinned to the viewport
  // with `fixed inset-0` (not just `h-screen`) so a tall page can never push
  // the document and drag the sidebar/toolbar -- only <main> scrolls.
  return (
    <div className="fixed inset-0 flex overflow-hidden bg-surface text-on-surface">
      <Sidebar />
      <GlobalLoadingIndicator />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <AnnouncementBanner />
        <ImportSuccessBanner />
        <ImportProgressBanner />
        <Toolbar />
        <main
          data-scroll-restoration-id="app-main"
          className="min-h-0 flex-1 overflow-y-auto overflow-x-auto"
        >
          {children}
        </main>
        <AppFooter />
      </div>
      <FloatingFeedbackButton />
    </div>
  );
}
